import type { Graph } from '@metro/graph'

/**
 * What A* reads per relaxation, laid out by the graph's vertex and edge
 * indices so the search builds and hashes no string: the weight and target
 * coordinates of each edge, and the order of the two cross-platform
 * tie-break keys.
 *
 * The tie-breaks compare strings — the queue pops equal priorities by vertex
 * key `id|line`, and an equal tentative cost keeps the predecessor with the
 * lower edge key `from.id|from.line|realID` (Android A*). Ranking each key
 * once among all keys of the graph turns every comparison into an integer one
 * with the same outcome. Built once per graph (`Graph.derived`). iOS twin:
 * `AStarIndex.swift`.
 */
export interface AStarIndex {
  /** Rank of each vertex key `id|line` among all vertex keys. */
  readonly vertexRank: Int32Array
  /** Rank of each edge key `from.id|from.line|realID`; equal keys share a rank. */
  readonly edgeTieRank: Int32Array
  readonly edgeWeight: Float64Array
  /** Coordinates of each edge's `to` vertex as iOS / Android hold them (float32). */
  readonly edgeToX: Float32Array
  readonly edgeToY: Float32Array
}

export function aStarIndexOf(graph: Graph): AStarIndex {
  return graph.derived('routing.aStarIndex', build)
}

function build(graph: Graph): AStarIndex {
  const vertexKeys: string[] = []
  for (let i = 0; i < graph.vertexCount; i++) {
    const v = graph.vertexAt(i)
    vertexKeys.push(`${v.id}|${v.lineID}`)
  }
  const tieKeys: string[] = []
  const edgeWeight = new Float64Array(graph.edgeCount)
  const edgeToX = new Float32Array(graph.edgeCount)
  const edgeToY = new Float32Array(graph.edgeCount)
  for (let i = 0; i < graph.edgeCount; i++) {
    const e = graph.edgeAt(i)
    tieKeys.push(`${e.from.id}|${e.from.lineID}|${e.realID}`)
    edgeWeight[i] = e.weight
    edgeToX[i] = e.to.x
    edgeToY[i] = e.to.y
  }
  return {
    vertexRank: ranks(vertexKeys),
    edgeTieRank: ranks(tieKeys),
    edgeWeight,
    edgeToX,
    edgeToY,
  }
}

/** Dense ranks in `<` order: equal strings get equal ranks. */
function ranks(keys: readonly string[]): Int32Array {
  const sorted = [...new Set(keys)].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
  const rankByKey = new Map<string, number>()
  sorted.forEach((key, rank) => rankByKey.set(key, rank))
  return Int32Array.from(keys, (key) => rankByKey.get(key) ?? 0)
}
